import { FC, Fragment, useState } from "react";
import { useMediaQuery } from "@mui/material";
import LinkComponent from "@Components/Link";
import { encodeProductsSearchCriteria } from "@Components/products-listing/utils";
import { SORT_BY } from "@Components/search/utils/algoliaData";
import { Button } from "@Components/ui";
import Icon from "@Components/ui/icon";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import { setItem } from "@Utils/browserStorage";
import useBodyScrollLock from "@wff/hooks/useBodyScrollLock";
import { shopMenuProps } from "@wff/interfaces";
import Image from "components/Image";

import classes from "./ShopPopOut.module.scss";

interface ShopPopOutProps {
  isOpen: boolean;
  onClose: () => void;
  menuData?: shopMenuProps[];
  currentLocale: string;
}

export const generateSubCategoryLink = (
  categoryLink: string,
  subcategoryLink: string
): string => {
  const subCategoryPath =
    subcategoryLink && subcategoryLink.split("/")?.reverse()?.[0];

  return `${categoryLink}?criteria=${encodeProductsSearchCriteria({
    sortBy: SORT_BY.NONE,
    filterBy: {
      subCategories: [String(subCategoryPath)],
    },
  })}`;
};

const ShopPopOut: FC<ShopPopOutProps> = ({
  isOpen,
  onClose,
  menuData,
  currentLocale,
}) => {
  const [activeIndex, setActiveIndex] = useState<string>("");

  useBodyScrollLock(isOpen);

  const joinClass = [
    classes["shop-pop"],
    classes[isOpen ? "shop-pop-active" : ""],
  ]
    .join(" ")
    .trim();

  const isSmallScreen = useMediaQuery("(max-width:1200px)");

  const hasSubMenu = (subMenu: {}[]) => subMenu?.length > 0;

  return (
    <div className={joinClass} style={{ top: isSmallScreen ? "30%" : "37%" }}>
      <div className={classes["shop-pop-content"]}>
        <div className={classes["shop-pop-content-scroll"]}>
          {menuData?.map((item, index) => (
            <div key={index}>
              <h3>{item?.categoryName}</h3>
              {item?.categorySubNavs?.items &&
                item?.categorySubNavs?.items?.map((list, index) => {
                  const isMenuHasSubItem = hasSubMenu(list?.menus?.items);

                  return (
                    <Fragment key={index}>
                      <div
                        onMouseEnter={() =>
                          isMenuHasSubItem &&
                          setActiveIndex(`${item?.categoryName}_${index}`)
                        }
                        className={[
                          classes["shop-pop-content-list"],
                          activeIndex == `${item?.categoryName}_${index}`
                            ? "shop-pop-content-list-active"
                            : "",
                        ].join(" ")}
                      >
                        <span>{list?.title}</span>
                        <Icon
                          iconName="arrow-right-thin"
                          className={classes["shop-pop-content-list-icon"]}
                        />
                      </div>
                      {activeIndex === `${item?.categoryName}_${index}` && (
                        <div
                          data-testid="shop-pop-content-sub"
                          className={classes["shop-pop-content-sub"]}
                          onMouseLeave={() => setActiveIndex("")}
                        >
                          <div
                            className={`${classes["shop-pop-content-scroll"]} ${classes["shop-pop-content-sub-menu"]}`}
                          >
                            {list?.menus?.items?.map((item, index) => (
                              <LinkComponent
                                href={generateSubCategoryLink(
                                  list?.link ?? "",
                                  item?.link
                                )}
                                key={index}
                              >
                                <p
                                  onClick={() => {
                                    onClose();
                                    setItem(
                                      STORAGE_KEYS.DY_PRODUCTS_SEARCH_CRITERIA,
                                      item?.title ?? "",
                                      STORAGE_TYPES.SESSION
                                    );
                                  }}
                                >
                                  {item?.title}
                                </p>
                              </LinkComponent>
                            ))}
                          </div>
                          {currentLocale === "en-US" && (
                            <div
                              className={classes["shop-pop-content-sub-image"]}
                            >
                              <Image
                                src={`${list?.previewImage?.url}?fm=webp&w=1024&h=768`}
                                alt="alt-img"
                                width={170}
                                height={200}
                              />
                              <h3>{list?.caption}</h3>
                              {list?.button && (
                                <LinkComponent href={list?.button?.link}>
                                  <a>
                                    <Button
                                      className={
                                        classes["shop-pop-content-sub-button"]
                                      }
                                    >
                                      {list?.button?.label}
                                    </Button>
                                  </a>
                                </LinkComponent>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </Fragment>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
      <div
        data-testid="overlay"
        className={classes["overlay"]}
        onClick={onClose}
      ></div>
    </div>
  );
};

ShopPopOut.defaultProps = {
  isOpen: false,
  menuData: [],
  currentLocale: "en-US",
};

export default ShopPopOut;
